import { GeneratorStatusExceedanceEventType } from "encamp-prisma/generated/browser";

/** Events that set a finding's review status, as opposed to annotating it. */
export const GENERATOR_REVIEW_DISPOSITION_TYPES = [
  GeneratorStatusExceedanceEventType.OPENED,
  GeneratorStatusExceedanceEventType.REOPENED,
  GeneratorStatusExceedanceEventType.AUTO_RESOLVED,
  GeneratorStatusExceedanceEventType.RESOLVED,
  GeneratorStatusExceedanceEventType.IGNORED,
  GeneratorStatusExceedanceEventType.PENDING,
] as const satisfies readonly GeneratorStatusExceedanceEventType[];

const dispositionTypes = new Set<string>(GENERATOR_REVIEW_DISPOSITION_TYPES);

export function isGeneratorReviewDisposition(eventType: string): boolean {
  return dispositionTypes.has(eventType);
}

type TimedEvent = { id: string; createdAt: Date | string };

/** Ids break timestamp ties so every surface orders review events the same way. */
export function compareGeneratorReviewEvents(a: TimedEvent, b: TimedEvent): number {
  return (
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.id.localeCompare(b.id)
  );
}

/** Dispositions recorded at the same instant can't be ordered, so neither is the decision. */
export function hasSimultaneousDisposition(
  lifecycle: readonly TimedEvent[],
  event: TimedEvent
): boolean {
  const at = new Date(event.createdAt).getTime();
  return lifecycle.some(
    (other) => other.id !== event.id && new Date(other.createdAt).getTime() === at
  );
}

type EvidenceSpan<T> = {
  periodStart: T;
  lastPeriodStart?: T | null;
  mismatchPeriodStarts?: readonly T[] | null;
};

/** Evidence can start before the immutable anchor month, but never after the latest month. */
export function firstEvidenceMonth<T extends Date | string>(finding: EvidenceSpan<T>): T {
  const first = finding.mismatchPeriodStarts?.[0] ?? finding.periodStart;
  const last = finding.lastPeriodStart;
  return last != null && new Date(last).getTime() < new Date(first).getTime() ? last : first;
}
